import { termMonths } from "../lib/utils";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getClients, getProductTypes, createOrder, createClient, uploadFile, getProposals, getProjects, getProject } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Loader2, CheckCircle2, Globe, Target, Sparkles, Search, ArrowRight, Calendar,
  Upload, FileText, X, Server, MapPin, ExternalLink, Link2, FileSignature,
} from "lucide-react";

const META_FORM_URL = "https://docs.google.com/document/d/1sWLywmRoG-5xWTnbqWxeEGM9a0QpU8L1ZkQmRa8iPek/edit?usp=sharing";

const PRODUCT_ICONS = { marketing: Target, web_design: Globe, seo_aeo: Search, prospect_tools: Sparkles };
const PRODUCT_COLORS = { marketing: "border-orange-400 bg-orange-50", web_design: "border-blue-400 bg-blue-50", seo_aeo: "border-green-400 bg-green-50", prospect_tools: "border-purple-400 bg-purple-50" };
const PRODUCT_ACCENT = { marketing: "text-orange-600", web_design: "text-blue-600", seo_aeo: "text-green-600", prospect_tools: "text-purple-600" };

/**
 * Normalize any axios/backend error into a plain string.
 * FastAPI returns `detail` as either a string (custom HTTPException) OR an
 * array of validation objects [{loc,msg,type,input,ctx}]. Rendering the array
 * directly in a toast or JSX throws React error #31 ("Objects are not valid
 * as a React child") which crashes the whole app to a blank white page.
 * Always pipe errors through this helper before display.
 */
function formatApiError(err, fallback = "Request failed") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    return d.map((e) => {
      if (typeof e === "string") return e;
      const field = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
      const msg = e?.msg || "invalid value";
      return field ? `${field}: ${msg}` : msg;
    }).join("; ");
  }
  if (d && typeof d === "object") {
    try { return JSON.stringify(d); } catch { /* ignore */ }
  }
  if (err?.message) return err.message;
  return fallback;
}

export default function NewOrderPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [searchParams] = useSearchParams();
  const preselectedClient = searchParams.get("client");
  const fileInputRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [signedContractFile, setSignedContractFile] = useState(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const contractInputRef = useRef(null);
  const [addingNewClient, setAddingNewClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ company: "", website: "", industry: "", name: "", email: "", phone: "" });
  const [creatingClient, setCreatingClient] = useState(false);

  // Renewal-from-existing-order state
  const [allProjects, setAllProjects] = useState([]);
  const [isRenewal, setIsRenewal] = useState(false);
  const [renewalSourceId, setRenewalSourceId] = useState("");

  const [form, setForm] = useState({
    client_id: preselectedClient || "",
    product_types: [],
    sub_products: [],
    notes: "",
    start_date: "",
    end_date: "",
    auto_renew: false,
    term_months: "",
    monthly_amount: "",
    contract_value: "",
    contract_value_type: "wholesale",  // "wholesale" | "retail"
    channel_quantities: {},
    ad_creative_source: "",
    page_count: "",
    hosting_option: "",
    file_ids: [],
    geo_target: "",
    demographics: "",
    use_prospect_id: false,
    prospect_count: "",
    proposal_id: "",
    signed_contract_file_id: "",
    proposal_changes_notes: "",
  });

  // Derived: whole months between start_date and end_date (inclusive of both ends).
  // Returns null if either date is missing or end < start. Drives the auto-calculated
  // Total Contract Value display.
  const monthsBetween = termMonths(form.start_date, form.end_date);
  // Rep-picked month count wins; otherwise fall back to the date range.
  const billableMonths = form.term_months ? Number(form.term_months) : monthsBetween;

  // Auto-computed total = monthly × months, only when fixed-term + valid inputs.
  const computedTotal = (() => {
    if (form.auto_renew) return null;
    const monthly = parseFloat(form.monthly_amount);
    if (!monthly || !billableMonths) return null;
    return monthly * billableMonths;
  })();

  useEffect(() => {
    Promise.all([getClients(), getProductTypes(), getProposals(), getProjects()])
      .then(([c, p, pr, pj]) => { setClients(c.data); setProductTypes(p.data); setProposals(pr.data || []); setAllProjects(pj.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCreateNewClient = async () => {
    const company = (newClientForm.company || "").trim();
    const email = (newClientForm.email || "").trim();
    if (!company || !email) {
      toast.error("Company name and email are required");
      return;
    }
    // Email format check — mirrors backend's EmailStr so we don't roundtrip a
    // 422 that used to crash the page. Basic RFC-lite validation is fine here.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address (e.g. name@company.com)");
      return;
    }
    setCreatingClient(true);
    try {
      const payload = { ...newClientForm, company, email };
      const { data } = await createClient(payload);
      setClients((prev) => [...prev, data]);
      setForm((prev) => ({ ...prev, client_id: data.id }));
      setAddingNewClient(false);
      setNewClientForm({ company: "", website: "", industry: "", name: "", email: "", phone: "" });
      toast.success(`"${data.company}" added!`);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to create client"));
    } finally {
      setCreatingClient(false);
    }
  };

  const toggleProductType = (typeId) => {
    setForm((prev) => {
      const has = prev.product_types.includes(typeId);
      const newTypes = has ? prev.product_types.filter((t) => t !== typeId) : [...prev.product_types, typeId];
      // Remove sub-products belonging to deselected category
      const validSubIds = new Set();
      newTypes.forEach((t) => {
        const pt = productTypes.find((p) => p.id === t);
        pt?.sub_products?.forEach((sp) => validSubIds.add(sp.id));
      });
      const newSubs = prev.sub_products.filter((s) => validSubIds.has(s));
      return { ...prev, product_types: newTypes, sub_products: newSubs };
    });
  };

  const toggleSubProduct = (subId) => {
    setForm((prev) => {
      const newSubs = prev.sub_products.includes(subId)
        ? prev.sub_products.filter((id) => id !== subId)
        : [...prev.sub_products, subId];
      const newQty = { ...prev.channel_quantities };
      if (!newSubs.includes(subId)) delete newQty[subId];
      return { ...prev, sub_products: newSubs, channel_quantities: newQty };
    });
  };

  const updateQty = (ch, field, val) => {
    setForm((prev) => ({
      ...prev,
      channel_quantities: { ...prev.channel_quantities, [ch]: { ...(prev.channel_quantities[ch] || {}), [field]: val } },
    }));
  };

  const prefillFromRenewal = async (sourceProjectId) => {
    if (!sourceProjectId) return;
    try {
      const { data: p } = await getProject(sourceProjectId);
      setForm((prev) => ({
        ...prev,
        product_types: p.product_types?.length ? p.product_types : (p.product_type ? [p.product_type] : []),
        sub_products: p.sub_products || [],
        channel_quantities: p.channel_quantities || {},
        ad_creative_source: p.ad_creative_source || "",
        geo_target: p.geo_target || "",
        demographics: p.demographics || "",
        use_prospect_id: p.use_prospect_id || false,
        prospect_count: p.prospect_count || "",
        hosting_option: p.hosting_option || "",
        page_count: p.page_count || "",
        auto_renew: p.auto_renew || false,
        term_months: p.term_months ? String(p.term_months) : "",
        monthly_amount: p.monthly_amount != null ? String(p.monthly_amount) : "",
        contract_value: p.contract_value != null ? String(p.contract_value) : "",
        contract_value_type: p.contract_value_type || "wholesale",
        notes: prev.notes ? prev.notes + "\n\n(Renewal of order from " + (p.created_at || "previous") + ")" : `Renewal of previous order — original products, targeting, and pricing pre-filled. Adjust as needed.`,
      }));
      toast.success("Order pre-filled from previous order — review & adjust");
    } catch (err) {
      toast.error("Could not load previous order to pre-fill");
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { data } = await uploadFile(file);
        setUploadedFiles((prev) => [...prev, data]);
        setForm((prev) => ({ ...prev, file_ids: [...prev.file_ids, data.id] }));
      }
      toast.success(`${files.length} file(s) uploaded`);
    } catch { toast.error("File upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removeFile = (fid) => {
    setUploadedFiles((p) => p.filter((f) => f.id !== fid));
    setForm((p) => ({ ...p, file_ids: p.file_ids.filter((id) => id !== fid) }));
  };

  const handleContractUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingContract(true);
    try {
      const { data } = await uploadFile(file);
      setSignedContractFile(data);
      setForm((prev) => ({ ...prev, signed_contract_file_id: data.id }));
      toast.success("Signed contract uploaded");
    } catch { toast.error("Contract upload failed"); }
    finally { setUploadingContract(false); if (contractInputRef.current) contractInputRef.current.value = ""; }
  };

  const removeContract = () => {
    setSignedContractFile(null);
    setForm((p) => ({ ...p, signed_contract_file_id: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // ---- Required-field validation -----------------------------------------
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (!form.product_types.length) { toast.error("Select at least one product category"); return; }
    if (!form.sub_products.length) { toast.error("Pick at least one specific product/item"); return; }
    if (!form.start_date) { toast.error("Set a start date"); return; }
    if (!form.monthly_amount) { toast.error("Enter the monthly amount"); return; }
    if (!form.auto_renew && !form.end_date) { toast.error("Set an end date (or pick Month-to-Month Auto-Renew)"); return; }
    if (!form.auto_renew && !billableMonths) { toast.error("Pick the number of months"); return; }
    
    // Marketing-specific requirements
    const hasMarketing = form.product_types.includes("marketing");
    if (hasMarketing) {
      if (!form.ad_creative_source) {
        toast.error("Marketing orders require an Ad Creative source (Attached, To Come, or RP to Build)");
        return;
      }
      if (!form.geo_target || !form.geo_target.trim()) {
        toast.error("Marketing orders require a Geo Target (e.g., city, state, ZIPs)");
        return;
      }
      if (!form.demographics || !form.demographics.trim()) {
        toast.error("Marketing orders require Demographics / Audience Targeting");
        return;
      }
      if (form.use_prospect_id && !form.prospect_count) {
        toast.error("Enter Total Prospects in List when using ProspectID targeting");
        return;
      }
      // Total impressions REQUIRED for every marketing sub-product.
      // Google Ads (SEM) additionally requires Estimated Clicks.
      const marketingSubs = form.sub_products.filter((s) => ["meta", "google", "tiktok", "youtube", "display", "programmatic", "linkedin"].includes(s));
      for (const sub of marketingSubs) {
        const q = form.channel_quantities[sub] || {};
        if (!q.impressions || !String(q.impressions).trim()) {
          toast.error(`Enter Total Impressions for ${sub.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}`);
          return;
        }
        if (sub === "google" && (!q.clicks || !String(q.clicks).trim())) {
          toast.error("SEM (Google Ads) requires Estimated Clicks");
          return;
        }
      }
    }
    
    // Web-design specific requirements
    const hasWebDesign = form.product_types.includes("web_design");
    if (hasWebDesign) {
      const needsHosting = form.sub_products.some((s) => ["landing_page", "multi_page", "custom_build"].includes(s));
      if (needsHosting && !form.hosting_option) {
        toast.error("Pick a Hosting option for Web Design");
        return;
      }
      if (form.sub_products.includes("multi_page") && !form.page_count) {
        toast.error("Enter the number of pages for the Multi-Page website");
        return;
      }
    }
    // ------------------------------------------------------------------------
    
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        page_count: form.page_count ? parseInt(form.page_count, 10) : null,
        monthly_amount: form.monthly_amount ? parseFloat(form.monthly_amount) : null,
        term_months: form.auto_renew ? null : billableMonths,
        // Total contract value: auto-computed for fixed-term, null for month-to-month.
        // Backend re-derives this server-side too, so this is just an optimistic hint.
        contract_value: form.auto_renew
          ? null
          : (computedTotal != null
              ? computedTotal
              : (form.contract_value ? parseFloat(form.contract_value) : null)),
        renewal_source_id: isRenewal && renewalSourceId ? renewalSourceId : null,
      };
      const { data } = await createOrder(payload);
      if (!data || !data.id) {
        // Should never happen — but if the backend returns an anomalous
        // response, DON'T navigate to /orders/undefined and go blank.
        const msg = "Order response missing id — please refresh and check Onboarding to see if the order was actually created.";
        console.error("[create-order] response missing id:", data);
        setError(msg);
        toast.error(msg);
        return;
      }
      setSuccess(true);
      toast.success("Order created!");
      setTimeout(() => navigate(`/orders/${data.id}`), 1500);
    } catch (err) {
      // Always surface the real backend reason — even if it's nested or not a string
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (typeof detail === "string" && detail.length > 0) {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]) {
        // FastAPI validation errors come back as a list of {loc, msg}
        msg = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg || d}`).join(" • ");
      } else if (detail && typeof detail === "object") {
        msg = JSON.stringify(detail);
      } else if (err.message) {
        msg = err.message;
      } else {
        msg = "Failed to create order";
      }
      const full = status ? `${msg} (HTTP ${status})` : msg;
      console.error("[create-order] failure:", { status, detail, raw: err });
      setError(full);
      toast.error(full);
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (success) return <div className="flex flex-col items-center justify-center h-[60vh]"><div className="p-4 rounded-full bg-green-100 mb-4"><CheckCircle2 className="h-12 w-12 text-green-600" /></div><h2 className="font-outfit text-2xl font-bold">Order Created!</h2><p className="text-muted-foreground mt-1">Redirecting to project...</p></div>;

  const hasMarketing = form.product_types.includes("marketing");
  const hasWebDesign = form.product_types.includes("web_design");
  const hasMultiPage = form.sub_products.includes("multi_page");
  const hasCustomBuild = form.sub_products.includes("custom_build");
  const hasMeta = form.sub_products.includes("meta");

  // Gather all sub-products from selected categories
  const allSubProducts = [];
  form.product_types.forEach((t) => {
    const pt = productTypes.find((p) => p.id === t);
    if (pt?.sub_products) allSubProducts.push(...pt.sub_products.map((sp) => ({ ...sp, category: t, categoryName: pt.name })));
  });

  // Group sub-products by category
  const subsByCategory = {};
  allSubProducts.forEach((sp) => {
    if (!subsByCategory[sp.category]) subsByCategory[sp.category] = { name: sp.categoryName, items: [] };
    subsByCategory[sp.category].items.push(sp);
  });

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="new-order-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Book New Order</h1>
        <p className="text-muted-foreground mt-1">Create a new order for your client</p>
      </div>

      {isSuperAdmin && (
        <Alert className="bg-amber-50 border-amber-300" data-testid="super-admin-order-warning">
          <AlertDescription className="text-amber-900 text-sm space-y-1">
            <p className="font-semibold">Heads up — Super Admins can't submit orders directly.</p>
            <p>
              Orders are booked by Reps (or Managers on behalf of a rep). To test the order flow,
              use the <strong>Users → View as</strong> action on a Rep, but note that impersonation
              is <em>read-only</em>. To actually create an order, log in as a Rep using a temp password
              (you can reset any rep's password from the Users page) or use <strong>Proposals & Audits</strong>
              if you're trying to share a proposed scope with the partner.
            </p>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
          {error && <Alert variant="destructive" data-testid="create-order-error"><AlertDescription className="whitespace-pre-wrap">{error}</AlertDescription></Alert>}

          {/* 1. Client — select existing or add new inline */}
          <Card className="border"><CardHeader><CardTitle className="text-lg">1. Client</CardTitle><CardDescription>Select an existing client or add a new one</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              {!addingNewClient ? (
                <>
                  <Select value={form.client_id} onValueChange={(v) => { setForm({ ...form, client_id: v }); setAddingNewClient(false); }}>
                    <SelectTrigger data-testid="client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name} {c.industry && `(${c.industry})`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-center">
                    <Button type="button" variant="link" className="text-primary" onClick={() => setAddingNewClient(true)} data-testid="add-new-client-toggle">
                      + Add a New Client
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3 p-4 rounded-lg border bg-secondary/30">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">New Client Details</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAddingNewClient(false)}>Cancel</Button>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Company Name *</Label>
                    <Input value={newClientForm.company} onChange={(e) => setNewClientForm({ ...newClientForm, company: e.target.value })} placeholder="Company name" data-testid="new-client-company" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Business Website URL</Label>
                    <Input value={newClientForm.website} onChange={(e) => setNewClientForm({ ...newClientForm, website: e.target.value })} placeholder="https://www.example.com" data-testid="new-client-website" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Industry</Label>
                    <Input value={newClientForm.industry} onChange={(e) => setNewClientForm({ ...newClientForm, industry: e.target.value })} placeholder="e.g., Roofing, Legal, HVAC" data-testid="new-client-industry" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs text-muted-foreground">Client Contact Name</Label>
                      <Input value={newClientForm.name} onChange={(e) => setNewClientForm({ ...newClientForm, name: e.target.value })} placeholder="Contact name" data-testid="new-client-name" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs text-muted-foreground">Client Email *</Label>
                      <Input type="email" value={newClientForm.email} onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })} placeholder="email@example.com" data-testid="new-client-email" />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs text-muted-foreground">Client Phone</Label>
                      <Input value={newClientForm.phone} onChange={(e) => setNewClientForm({ ...newClientForm, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="new-client-phone" />
                    </div>
                  </div>
                  <Button type="button" onClick={handleCreateNewClient} disabled={creatingClient} className="w-full" data-testid="save-new-client">
                    {creatingClient ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><CheckCircle2 className="mr-2 h-4 w-4" />Save Client & Continue</>}
                  </Button>
                </div>
              )}
              {form.client_id && !addingNewClient && (
                <div className="flex items-center gap-2 p-2 rounded bg-green-50 border border-green-200 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-800">Client selected: <strong>{clients.find((c) => c.id === form.client_id)?.company || clients.find((c) => c.id === form.client_id)?.name}</strong></span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 1a. New Order or Renewal? */}
          {form.client_id && !addingNewClient && (() => {
            const selectedClient = clients.find((c) => c.id === form.client_id);
            const clientProjects = allProjects.filter((p) => p.client_id === form.client_id);
            if (clientProjects.length === 0) return null;
            return (
              <Card className="border border-amber-200 bg-amber-50/40" data-testid="renewal-card">
                <CardHeader>
                  <CardTitle className="text-base">Is this a New Order or a Renewal?</CardTitle>
                  <CardDescription>
                    {selectedClient?.company || selectedClient?.name} has {clientProjects.length} existing order{clientProjects.length === 1 ? "" : "s"} in the portal.
                    If this is a renewal, we'll pre-fill the previous products and targeting so you don't have to retype everything.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <RadioGroup
                    value={isRenewal ? "renewal" : "new"}
                    onValueChange={(v) => {
                      const renewal = v === "renewal";
                      setIsRenewal(renewal);
                      if (!renewal) setRenewalSourceId("");
                    }}
                    className="flex gap-6"
                  >
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <RadioGroupItem value="new" data-testid="order-mode-new" />
                      <span>New Order (different scope)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <RadioGroupItem value="renewal" data-testid="order-mode-renewal" />
                      <span>Renewal of a previous order</span>
                    </label>
                  </RadioGroup>
                  {isRenewal && (
                    <div className="space-y-2 pt-2 border-t border-amber-200">
                      <Label className="text-sm">Pick the order to renew</Label>
                      <Select
                        value={renewalSourceId}
                        onValueChange={(v) => { setRenewalSourceId(v); prefillFromRenewal(v); }}
                      >
                        <SelectTrigger data-testid="renewal-source-select">
                          <SelectValue placeholder="Choose a previous order..." />
                        </SelectTrigger>
                        <SelectContent>
                          {clientProjects.map((p) => {
                            const items = p.sub_product_names?.length ? p.sub_product_names.join(", ") : (p.product_types || []).join(", ");
                            const dateRange = `${p.start_date || "?"}${p.end_date ? " → " + p.end_date : ""}`;
                            return (
                              <SelectItem key={p.id} value={p.id}>
                                {items} — {dateRange}{p.monthly_amount ? ` · $${Number(p.monthly_amount).toLocaleString()}/mo` : (p.contract_value ? ` · $${Number(p.contract_value).toLocaleString()} total` : "")}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        We'll copy products, channels, impressions/clicks, targeting, ad creative source, hosting, and contract value.
                        Review every field below and adjust before submitting.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* 1b. Proposal & Contract (optional) */}
          {form.client_id && (
            <Card className="border" data-testid="proposal-contract-card">
              <CardHeader>
                <CardTitle className="text-lg">Proposal & Contract <span className="text-xs font-normal text-muted-foreground">(optional)</span></CardTitle>
                <CardDescription>Link the original proposal, attach the signed contract, and note any changes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Link existing proposal */}
                <div>
                  <Label className="text-sm flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" />Link to Proposal</Label>
                  <Select value={form.proposal_id || "__none__"} onValueChange={(v) => setForm({ ...form, proposal_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="proposal-link-select">
                      <SelectValue placeholder="No linked proposal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No linked proposal —</SelectItem>
                      {(() => {
                        const selectedClient = clients.find((c) => c.id === form.client_id);
                        const company = (selectedClient?.company || selectedClient?.name || "").toLowerCase();
                        const matched = proposals.filter((p) => company && p.business_name?.toLowerCase().includes(company));
                        const others = proposals.filter((p) => !matched.includes(p));
                        return [
                          ...matched.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              ★ {p.business_name} — {p.status} (${(p.estimated_value || 0).toLocaleString()})
                            </SelectItem>
                          )),
                          ...others.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.business_name} — {p.status} (${(p.estimated_value || 0).toLocaleString()})
                            </SelectItem>
                          )),
                        ];
                      })()}
                    </SelectContent>
                  </Select>
                  {proposals.length === 0 && <p className="text-xs text-muted-foreground mt-1">No proposals available to link.</p>}
                </div>

                {/* Signed Contract Upload */}
                <div>
                  <Label className="text-sm flex items-center gap-1.5"><FileSignature className="h-3.5 w-3.5" />Signed Contract</Label>
                  {signedContractFile ? (
                    <div className="mt-1 flex items-center justify-between p-2 rounded border bg-green-50/50">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileSignature className="h-4 w-4 text-green-700 shrink-0" />
                        <span className="text-sm truncate">{signedContractFile.filename}</span>
                        <span className="text-xs text-muted-foreground shrink-0">({Math.round(signedContractFile.size / 1024)} KB)</span>
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={removeContract} data-testid="remove-contract-btn">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => contractInputRef.current?.click()} disabled={uploadingContract} data-testid="upload-contract-btn">
                      {uploadingContract ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Uploading...</> : <><Upload className="mr-2 h-3 w-3" />Upload Signed Contract</>}
                    </Button>
                  )}
                  <input ref={contractInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={handleContractUpload} data-testid="contract-file-input" />
                </div>

                {/* Changes from original proposal */}
                <div>
                  <Label className="text-sm">Changes from Original Proposal</Label>
                  <Textarea
                    value={form.proposal_changes_notes}
                    onChange={(e) => setForm({ ...form, proposal_changes_notes: e.target.value })}
                    placeholder="Please provide any changes we should know about to the original proposal..."
                    rows={3}
                    className="mt-1"
                    data-testid="proposal-changes-notes"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* 2. Product Categories (multi-select) */}
          <Card className="border"><CardHeader><CardTitle className="text-lg">2. Select Product Categories</CardTitle><CardDescription>You can select multiple categories for this order</CardDescription></CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                {productTypes.map((product) => {
                  const Icon = PRODUCT_ICONS[product.id] || Globe;
                  const sel = form.product_types.includes(product.id);
                  return (
                    <button key={product.id} type="button" onClick={() => toggleProductType(product.id)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${sel ? `${PRODUCT_COLORS[product.id]}` : "border-border hover:border-primary/50 bg-card"}`}
                      data-testid={`product-${product.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${sel ? "bg-white" : "bg-secondary"}`}>
                          <Icon className={`h-5 w-5 ${sel ? PRODUCT_ACCENT[product.id] : "text-muted-foreground"}`} />
                        </div>
                        <div className="flex-1">
                          <span className="font-medium block">{product.name}</span>
                          <span className="text-xs text-muted-foreground">{product.sub_products?.length || 0} options</span>
                        </div>
                        {sel && <CheckCircle2 className="h-5 w-5 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* 3. Sub-Products grouped by category */}
          {Object.keys(subsByCategory).length > 0 && (
            <Card className="border animate-fade-in-up"><CardHeader><CardTitle className="text-lg">3. Select Products</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(subsByCategory).map(([catId, cat]) => (
                  <div key={catId}>
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">{cat.name}</h4>
                    <div className="space-y-3">
                      {cat.items.map((sub) => {
                        const checked = form.sub_products.includes(sub.id);
                        return (
                          <div key={sub.id}>
                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "bg-primary/5 border-primary/30" : "hover:bg-secondary/50 border-border"}`}>
                              <Checkbox checked={checked} onCheckedChange={() => toggleSubProduct(sub.id)} data-testid={`sub-product-${sub.id}`} />
                              <span className={`font-medium ${checked ? "" : "text-muted-foreground"}`}>{sub.name}</span>
                              {checked && <Badge className="ml-auto bg-primary/10 text-primary border-0">Selected</Badge>}
                            </label>

                            {/* Per-channel quantity for Marketing */}
                            {catId === "marketing" && checked && (
                              <div className="ml-10 mt-2 p-3 bg-secondary/30 rounded-lg space-y-3">
                                {sub.id === "google" ? (
                                  <>
                                    <div className="rounded bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 leading-relaxed">
                                      <strong>SEM Order:</strong> Attach a copy of your SEM quote here if you have it, and enter the total estimated impressions and clicks based on the proposal we provided.
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Attach SEM Quote (optional)</Label>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => fileInputRef.current?.click()}
                                          disabled={uploading}
                                          data-testid="sem-quote-upload-btn"
                                        >
                                          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                                          {uploading ? "Uploading..." : "Upload SEM Quote"}
                                        </Button>
                                        {uploadedFiles.length > 0 && (
                                          <span className="text-xs text-muted-foreground">
                                            {uploadedFiles.length} file{uploadedFiles.length === 1 ? "" : "s"} attached
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <Label className="text-xs text-muted-foreground">Total Impressions <span className="text-red-500">*</span></Label>
                                        <Input
                                          required
                                          type="text"
                                          placeholder="e.g., 100,000"
                                          value={form.channel_quantities[sub.id]?.impressions || ""}
                                          onChange={(e) => updateQty(sub.id, "impressions", e.target.value)}
                                          data-testid={`qty-${sub.id}-impressions`}
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-muted-foreground">Estimated Clicks <span className="text-red-500">*</span></Label>
                                        <Input
                                          required
                                          type="text"
                                          placeholder="e.g., 5,000"
                                          value={form.channel_quantities[sub.id]?.clicks || ""}
                                          onChange={(e) => updateQty(sub.id, "clicks", e.target.value)}
                                          data-testid={`qty-${sub.id}-clicks`}
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Total Monthly Impressions <span className="text-red-500">*</span></Label>
                                    <Input
                                      required
                                      type="text"
                                      placeholder="e.g., 250,000"
                                      value={form.channel_quantities[sub.id]?.impressions || ""}
                                      onChange={(e) => updateQty(sub.id, "impressions", e.target.value)}
                                      data-testid={`qty-${sub.id}-impressions`}
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Multi-page: page count */}
                            {sub.id === "multi_page" && checked && (
                              <div className="ml-10 mt-2 p-3 bg-secondary/30 rounded-lg">
                                <Label className="text-xs text-muted-foreground">Number of Pages</Label>
                                <Input type="number" min="2" placeholder="e.g., 5" value={form.page_count} onChange={(e) => setForm({ ...form, page_count: e.target.value })} data-testid="page-count-input" />
                              </div>
                            )}

                            {/* Custom build: proposal upload */}
                            {sub.id === "custom_build" && checked && (
                              <div className="ml-10 mt-2 p-3 bg-secondary/30 rounded-lg">
                                <Label className="text-xs text-muted-foreground">Upload Web Proposal</Label>
                                <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                                  {uploading ? "Uploading..." : "Upload Proposal"}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Hosting for Web Design */}
          {hasWebDesign && form.sub_products.some((s) => ["landing_page", "multi_page", "custom_build"].includes(s)) && (
            <Card className="border animate-fade-in-up"><CardHeader><CardTitle className="text-lg flex items-center gap-2"><Server className="h-5 w-5" /> Hosting Option <span className="text-red-500">*</span></CardTitle><CardDescription>Required for landing pages, multi-page sites, and custom builds</CardDescription></CardHeader>
              <CardContent>
                <RadioGroup value={form.hosting_option} onValueChange={(v) => setForm({ ...form, hosting_option: v })} className="space-y-3">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.hosting_option === "hosting_only" ? "bg-primary/5 border-primary/30" : "hover:bg-secondary/50"}`}>
                    <RadioGroupItem value="hosting_only" data-testid="hosting-only" />
                    <div><span className="font-medium">Hosting Only</span><p className="text-xs text-muted-foreground">Standard website hosting</p></div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.hosting_option === "hosting_with_maintenance" ? "bg-primary/5 border-primary/30" : "hover:bg-secondary/50"}`}>
                    <RadioGroupItem value="hosting_with_maintenance" data-testid="hosting-with-maintenance" />
                    <div><span className="font-medium">Hosting with Maintenance</span><p className="text-xs text-muted-foreground">Hosting plus ongoing site maintenance and updates</p></div>
                  </label>
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {/* Ad Creative for Marketing */}
          {hasMarketing && form.sub_products.length > 0 && (
            <Card className="border animate-fade-in-up"><CardHeader><CardTitle className="text-lg">Ad Creative <span className="text-red-500">*</span></CardTitle><CardDescription>Required — where will we get the ad creative?</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={form.ad_creative_source} onValueChange={(v) => setForm({ ...form, ad_creative_source: v })} className="space-y-3">
                  {[{ v: "attached", l: "See Creatives Attached" }, { v: "to_come", l: "Creatives to Come" }, { v: "rp_builds", l: "ReadyProspect to Build the Ads" }].map((opt) => (
                    <label key={opt.v} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.ad_creative_source === opt.v ? "bg-primary/5 border-primary/30" : "hover:bg-secondary/50"}`}>
                      <RadioGroupItem value={opt.v} data-testid={`creative-${opt.v}`} /><span className="font-medium">{opt.l}</span>
                    </label>
                  ))}
                </RadioGroup>
                {form.ad_creative_source === "attached" && (
                  <div className="p-4 border rounded-lg bg-secondary/30 space-y-3">
                    <Label className="text-sm font-medium">Upload Ad Creatives</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{uploading ? "Uploading..." : "Choose Files"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Marketing Targeting Criteria */}
          {hasMarketing && form.sub_products.length > 0 && (
            <Card className="border animate-fade-in-up"><CardHeader><CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5" /> Marketing Targeting Criteria</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>GEO Target <span className="text-red-500">*</span></Label>
                  <Input placeholder="Enter zip, city, metro, state..." value={form.geo_target} onChange={(e) => setForm({ ...form, geo_target: e.target.value })} data-testid="geo-target-input" required={hasMarketing} />
                </div>
                <div className="space-y-2">
                  <Label>Demographics / Audience Targeting <span className="text-red-500">*</span></Label>
                  <Textarea placeholder="Enter age, gender, homeowner, income targeting and more..." value={form.demographics} onChange={(e) => setForm({ ...form, demographics: e.target.value })} rows={3} data-testid="demographics-input" required={hasMarketing} />
                </div>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.use_prospect_id ? "bg-primary/5 border-primary/30" : "hover:bg-secondary/50"}`}>
                  <Checkbox checked={form.use_prospect_id} onCheckedChange={(v) => setForm({ ...form, use_prospect_id: !!v })} data-testid="prospect-id-checkbox" />
                  <div><span className="font-medium">Use ProspectID / Buyer Intent Data for Targeting</span><p className="text-xs text-muted-foreground">Leverage in-market buyer intent data for advanced targeting</p></div>
                </label>
                {form.use_prospect_id && (
                  <div className="ml-9 mt-2 p-3 bg-primary/5 rounded-lg border border-primary/20 space-y-2 animate-fade-in-up">
                    <Label className="text-xs text-muted-foreground">Total Prospects in List <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="e.g., 25000"
                      value={form.prospect_count || ""}
                      onChange={(e) => setForm({ ...form, prospect_count: e.target.value })}
                      data-testid="prospect-count-input"
                    />
                    <p className="text-xs text-muted-foreground">How many prospects are in the ProspectID list we'll be targeting?</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <Card className="border"><CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Attached Files ({uploadedFiles.length})</CardTitle></CardHeader>
              <CardContent><div className="space-y-2">{uploadedFiles.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-2 rounded border bg-secondary/30">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="text-sm">{f.filename}</span><span className="text-xs text-muted-foreground">({Math.round(f.size / 1024)} KB)</span></div>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(f.id)}><X className="h-3 w-3" /></Button>
                </div>
              ))}</div></CardContent>
            </Card>
          )}
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.zip,.doc,.docx,.ppt,.pptx" onChange={handleFileUpload} className="hidden" data-testid="file-input" />

          {/* Campaign Term & Contract Value */}
          <Card className="border"><CardHeader><CardTitle className="text-lg flex items-center gap-2"><Calendar className="h-5 w-5" /> Campaign Term</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Term type radio */}
              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, auto_renew: false })}
                  data-testid="term-fixed-btn"
                  className={`text-left p-3 rounded-lg border-2 transition-all ${!form.auto_renew ? "border-primary bg-primary/5" : "border-zinc-200 hover:border-zinc-300"}`}
                >
                  <div className="font-semibold text-sm">Fixed Term (Start - End)</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Defined start and end dates</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, auto_renew: true, end_date: "" })}
                  data-testid="term-auto-renew-btn"
                  className={`text-left p-3 rounded-lg border-2 transition-all ${form.auto_renew ? "border-primary bg-primary/5" : "border-zinc-200 hover:border-zinc-300"}`}
                >
                  <div className="font-semibold text-sm">Month-to-Month Auto-Renew</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Renews monthly until cancelled</div>
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="start-date-input" />
                </div>
                {!form.auto_renew && (
                  <div className="space-y-2">
                    <Label>End Date *</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} min={form.start_date || undefined} data-testid="end-date-input" required={!form.auto_renew} />
                  </div>
                )}
                {!form.auto_renew && (
                  <div className="space-y-2">
                    <Label>Number of Months *</Label>
                    <Select value={form.term_months || (monthsBetween ? String(monthsBetween) : "")} onValueChange={(v) => setForm({ ...form, term_months: v })}>
                      <SelectTrigger data-testid="term-months-select"><SelectValue placeholder="Pick months" /></SelectTrigger>
                      <SelectContent>
                        {[...Array(24)].map((_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{i + 1} month{i ? "s" : ""}</SelectItem>)}
                        <SelectItem value="36">36 months</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.term_months && monthsBetween && Number(form.term_months) !== monthsBetween ? (
                      <p className="text-[11px] text-amber-700 leading-snug" data-testid="term-months-mismatch">
                        Dates cover {monthsBetween} month{monthsBetween === 1 ? "" : "s"} — the total uses the {form.term_months} you picked.
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground leading-snug">Filled in from the dates — change it if the billing term is different.</p>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Monthly Amount <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="$ per month"
                    value={form.monthly_amount}
                    onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })}
                    data-testid="monthly-amount-input"
                  />
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Enter the recurring monthly charge — not the contract total.
                  </p>
                  <RadioGroup
                    value={form.contract_value_type}
                    onValueChange={(v) => setForm({ ...form, contract_value_type: v })}
                    className="flex flex-col gap-2 pt-1"
                  >
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <RadioGroupItem value="wholesale" data-testid="contract-value-type-wholesale" />
                      <span>Wholesale</span>
                    </label>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <RadioGroupItem value="retail" className="mt-0.5" data-testid="contract-value-type-retail" />
                      <div>
                        <span>Retail</span>
                        {form.contract_value_type === "retail" && (
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            If retail price is listed, we&apos;ll assume this is the normal two-time markup over wholesale.
                            If your markup is different, please indicate that in the notes below.
                          </p>
                        )}
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </div>

              {/* Auto-computed Total Contract Value — read-only summary so reps see exactly
                  what the system is going to record. Hidden for month-to-month since the
                  user explicitly wants that left blank. */}
              {!form.auto_renew && (
                <div className="rounded-lg border bg-emerald-50/40 border-emerald-200 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="total-contract-value-block">
                  <div className="text-sm">
                    <div className="font-semibold text-emerald-900">Total Contract Value</div>
                    <div className="text-xs text-emerald-800/80 mt-0.5">
                      {billableMonths && form.monthly_amount
                        ? <>Monthly amount × <b>{billableMonths}</b> month{billableMonths === 1 ? "" : "s"}</>
                        : <>Add a monthly amount and number of months to see the total auto-calculated.</>}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-emerald-900" data-testid="total-contract-value-display">
                    {computedTotal != null ? `$${Number(computedTotal).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : "—"}
                  </div>
                </div>
              )}
              {form.auto_renew && form.monthly_amount && (
                <div className="rounded-lg border bg-sky-50/40 border-sky-200 p-3 text-sm text-sky-900" data-testid="month-to-month-note">
                  <b>Month-to-month:</b> total contract value is intentionally left blank — only the monthly amount is tracked.
                </div>
              )}
              {form.auto_renew && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⓘ Renewal alerts will not fire automatically since this contract auto-renews. The rep can mark it as ending later if cancelled.
                </p>
              )}
              {!form.auto_renew && form.end_date && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                  ⓘ A renewal alert will email the rep + manager 30 days before the end date.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border"><CardHeader><CardTitle className="text-lg">Additional Notes</CardTitle></CardHeader>
            <CardContent><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Enter any additional details..." rows={4} data-testid="order-notes-input" /></CardContent>
          </Card>

          {/* Summary */}
          {form.client_id && form.product_types.length > 0 && (
            <Card className="border bg-secondary/30 animate-fade-in-up"><CardHeader><CardTitle className="text-lg">Order Summary</CardTitle></CardHeader>
              <CardContent><div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="font-medium">{clients.find((c) => c.id === form.client_id)?.company || clients.find((c) => c.id === form.client_id)?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Products</span><div className="flex flex-wrap gap-1 justify-end">{form.product_types.map((t) => <Badge key={t} variant="outline" className="text-xs">{productTypes.find((p) => p.id === t)?.name}</Badge>)}</div></div>
                {form.sub_products.length > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Items</span><div className="flex flex-wrap gap-1 justify-end">{form.sub_products.map((sp) => { const s = allSubProducts.find((x) => x.id === sp); return <Badge key={sp} variant="outline" className="text-xs">{s?.name}</Badge>; })}</div></div>}
                {form.hosting_option && <div className="flex justify-between"><span className="text-muted-foreground">Hosting</span><span className="font-medium">{form.hosting_option === "hosting_only" ? "Hosting Only" : "Hosting + Maintenance"}</span></div>}
                {form.ad_creative_source && <div className="flex justify-between"><span className="text-muted-foreground">Ad Creative</span><span className="font-medium">{form.ad_creative_source === "attached" ? "Attached" : form.ad_creative_source === "to_come" ? "To Come" : "RP to Build"}</span></div>}
                {form.geo_target && <div className="flex justify-between"><span className="text-muted-foreground">Geo Target</span><span className="font-medium">{form.geo_target}</span></div>}
                {form.use_prospect_id && <div className="flex justify-between"><span className="text-muted-foreground">ProspectID</span><Badge className="bg-primary/10 text-primary border-0">Enabled</Badge></div>}
                {form.start_date && <div className="flex justify-between"><span className="text-muted-foreground">Term</span><span className="font-medium">{form.start_date} → {form.auto_renew ? "Auto-Renew Monthly" : (form.end_date || "ongoing")}</span></div>}
                {form.monthly_amount && <div className="flex justify-between"><span className="text-muted-foreground">Monthly Amount</span><span className="font-medium">${Number(form.monthly_amount).toLocaleString()}</span></div>}
                {!form.auto_renew && computedTotal != null && <div className="flex justify-between"><span className="text-muted-foreground">Total Contract Value</span><span className="font-semibold">${Number(computedTotal).toLocaleString()}</span></div>}
                {form.auto_renew && form.monthly_amount && <div className="flex justify-between"><span className="text-muted-foreground">Total Contract Value</span><span className="text-xs italic text-muted-foreground">Month-to-month (not totalled)</span></div>}
                {form.proposal_id && <div className="flex justify-between"><span className="text-muted-foreground">Linked Proposal</span><span className="font-medium truncate max-w-[200px]">{proposals.find((p) => p.id === form.proposal_id)?.business_name}</span></div>}
                {signedContractFile && <div className="flex justify-between"><span className="text-muted-foreground">Signed Contract</span><Badge className="bg-green-100 text-green-800 border-0">Attached</Badge></div>}
                {uploadedFiles.length > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Files</span><span className="font-medium">{uploadedFiles.length} attached</span></div>}
              </div></CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-order-button">Cancel</Button>
            <Button type="submit" disabled={submitting || isSuperAdmin || !form.client_id || !form.product_types.length} data-testid="submit-order-button">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : <>Create Order<ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        </form>
    </div>
  );
}
